import { useCallback } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { ConfigProvider, type TableColumnType } from 'antd';
import { useFela } from 'react-fela';
import { FormattedMessage } from 'react-intl';

import type {
    AdministrationGroup,
    AdministrationUser,
    ListGroupsAdministrationQueryError,
    ListGroupsAdministrationQueryResult,
    ListUsersAdministrationQueryError,
    ListUsersAdministrationQueryResult,
} from '@/modules/api/codegen';
import { Alert, SortIcon, Table } from '@/modules/ui';

import { UserAdministrationType } from '../../constants';
import { useUserAdministrationStore } from '../../hooks/useUserAdministrationStore';
import { mapAntdSorterToApiSort } from '../../utils';
import { useIsAdministrationTableRecordCurrentUser } from './hooks';
import { useIsTableDataLoading } from './hooks/useIsTableDataLoading';
import * as felaRules from './UserAdministrationTable.rules';

export type UserAdministrationTableProps =
    | {
          type: UserAdministrationType.Users;
          columns: TableColumnType<AdministrationUser>[];
          query: UseQueryResult<ListUsersAdministrationQueryResult, ListUsersAdministrationQueryError>;
      }
    | {
          type: UserAdministrationType.Groups;
          columns: TableColumnType<AdministrationGroup>[];
          query: UseQueryResult<ListGroupsAdministrationQueryResult, ListGroupsAdministrationQueryError>;
      };

export const UserAdministrationTable = ({ type, columns, query }: UserAdministrationTableProps) => {
    const { css } = useFela();

    const { pagination, setPagination, setSort, resetSort, selectedRows, setSelectedRows } = useUserAdministrationStore();
    const isLoading = useIsTableDataLoading(query.isLoading, type);

    const handlePageChange = useCallback(
        (page: number, pageSize: number) => {
            setPagination({ type, pagination: { limit: pageSize, page } });
        },
        [type, setPagination],
    );

    const isCurrentUser = useIsAdministrationTableRecordCurrentUser();

    return (
        <div className={css(felaRules.container)}>
            <ConfigProvider
                renderEmpty={() =>
                    query.error ? (
                        <Alert
                            type='error'
                            title={<FormattedMessage id={`user-administration.table.${type}.loadingError`} />}
                            customStyle={felaRules.errorAlert}
                        />
                    ) : undefined
                }
            >
                <Table<AdministrationUser | AdministrationGroup>
                    columns={columns.map(column => ({
                        ...column,
                        showSorterTooltip: false,
                        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
                    }))}
                    dataSource={query.data?.data?.edges?.map((item, index) => ({
                        ...item,
                        key: item.id!,
                    }))}
                    rowSelection={{
                        preserveSelectedRowKeys: true,
                        selectedRowKeys: selectedRows[type],
                        onChange: selectedRowsKeys => {
                            setSelectedRows({ type, selectedRowsKeys });
                        },
                        getCheckboxProps(record) {
                            if (type === UserAdministrationType.Users && isCurrentUser(record))
                                return {
                                    disabled: true,
                                };

                            return {};
                        },
                    }}
                    pagination={{
                        pageSize: pagination[type].limit,
                        total: query.data?.data?.pageInfo?.count,
                        current: query.data?.data?.pageInfo?.page,
                        onChange: handlePageChange,
                        showSizeChanger: true,
                    }}
                    onChange={(_pagination, _filters, sorter, _extra) => {
                        if ('field' in sorter) {
                            if (sorter.field === undefined) resetSort(type);

                            const sort = mapAntdSorterToApiSort(sorter, type);
                            if (sort) setSort({ type, sort });
                        }
                    }}
                    loading={isLoading}
                />
            </ConfigProvider>
        </div>
    );
};
